import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  RootReducerState,
  getIsSettingEnabled,
  getUserMe,
} from '../../../../apps/main/rootReducer';
import { ProjectId, ProjectTabViews } from '../../../../models/Types';
import ProjectsNavigationSkeleton from '../ProjectsNavigationSkeleton';
import { AutoSizer, Index, List } from 'react-virtualized';
import { distinct } from '../../../../util';
import ProjectDropDownNavigationItem from './ProjectDropDownNavigationItem';
import useProjectSearch from '../../hooks/useProjectSearch';
import { useTranslation } from 'react-i18next';
import { makePrioStyles } from '../../../../theme/utils';
import { Project, ProjectNewsCounts } from '../../../../models/Project';
import { getProjectAccess } from '../../utils';
import useProjectsContext from '../../hooks/useProjectsContext';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    overflow: 'hidden',
  },
  noContent: {
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
}));

const useProjectCollapsible: (
  projectIds: ProjectId[],
  ref: React.MutableRefObject<any>
) => [
  {
    [projectId: ProjectId]: boolean;
  },
  {
    [projectId: ProjectId]: ProjectId[];
  },
  (projectId: ProjectId, collapsed: boolean) => void,
] = (projectIds, ref) => {
  const { getProjectById } = useProjectsContext();

  const [collapsedParentProjects, setCollapsedParentProjects] = useState<{
    [projectId: ProjectId]: boolean;
  }>({});

  const parentProjectSubProjects = useMemo(
    () =>
      projectIds.reduce((map, id) => {
        const project = getProjectById(id);
        if (project.parentProject) {
          map[project.parentProject] = distinct([
            ...(map[project.parentProject] ?? []),
            project.projectId,
          ]);
        }
        return map;
      }, {}),
    [projectIds, getProjectById]
  );

  const setCollapseParentProject = useCallback(
    (projectId: ProjectId, collapsed: boolean) => {
      setCollapsedParentProjects({
        ...collapsedParentProjects,
        [projectId]: collapsed,
      });
      ref?.current?.recomputeRowHeights();
      ref?.current?.forceUpdate();
    },
    [collapsedParentProjects, ref]
  );

  useEffect(() => {
    const _projectIds = distinct(
      projectIds
        .filter((id) => getProjectById(id).parentProject)
        .map((id) => getProjectById(id).parentProject)
    );
    const currentProjectIds = Object.keys(collapsedParentProjects);
    const idDiff = _projectIds.filter(
      (projectId) => !currentProjectIds.includes(projectId)
    );
    if (idDiff.length > 0) {
      setCollapsedParentProjects(
        projectIds
          .filter((id) => getProjectById(id).parentProject)
          .reduce((map, id) => {
            const project = getProjectById(id);
            return {
              ...map,
              [project.parentProject]:
                collapsedParentProjects[project.parentProject] ??
                projectIds.includes(project.parentProject),
            };
          }, {})
      );
    }
  }, [projectIds, getProjectById, collapsedParentProjects]);

  return [
    collapsedParentProjects,
    parentProjectSubProjects,
    setCollapseParentProject,
  ];
};

interface ProjectDropDownNavigationProps {
  activeProjectId?: string;
  selectedSubModule?: string;
  type?: ProjectTabViews;
  searchString?: string;
  isSubMenu?: boolean;
  pathPrefix?: string;
  isSearching?: boolean;
  activeTab?: string;
  archivedProjectsHidden?: boolean;
  getProjectNewsCountsByProjectId?: (projectId: ProjectId) => ProjectNewsCounts;
}

export interface ProjectDropDownNavigationRefProps {
  getCurrentProjects: () => Project[];
}

export const ProjectDropDownNavigation = React.memo(
  React.forwardRef<
    ProjectDropDownNavigationRefProps,
    ProjectDropDownNavigationProps
  >((props, componentRef) => {
    //#region ------------------------------ Defaults
    const {
      activeProjectId,
      selectedSubModule,
      type,
      searchString,
      isSubMenu,
      pathPrefix,
      isSearching,
      activeTab,
      archivedProjectsHidden,
      getProjectNewsCountsByProjectId,
    } = props;
    const ref = useRef(null);
    const { t } = useTranslation();
    const classes = useStyles();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const userMe = useSelector(getUserMe);
    const userMeProjectRoles = useSelector(getUserMe)?.prioData?.projectRoles;
    const userMeProjectIds = Object.keys(userMeProjectRoles ?? {});

    const myProjectIdsSet = useMemo(
      () => new Set(userMeProjectIds),
      [userMeProjectIds]
    );

    const { getProjectById } = useProjectsContext();

    const [projectIds, isFetching] = useProjectSearch({
      searchTerm: searchString,
      type: isSearching ? 'all' : type,
      archivedProjectsHidden: archivedProjectsHidden,
    });

    const [
      collapsedParentProjects,
      parentProjectSubProjects,
      setCollapseParentProject,
    ] = useProjectCollapsible(projectIds, ref);

    // necessary for re-rendering the list!
    const activeProject = useMemo(
      () => getProjectById(activeProjectId),
      [getProjectById, activeProjectId]
    );

    const isFinanceEnabled = useSelector<RootReducerState, boolean>((state) =>
      getIsSettingEnabled(state, 'General_FinanceServicesEnabled')
    );
    //#endregion

    //#region ------------------------------ Methods / Handlers
    const calcRowHeight: (params: Index) => number = useCallback(
      ({ index }) => {
        const project = getProjectById(projectIds[index]);
        if (
          !project ||
          (!isSearching && type === 'favorites' && !project.favorite)
        ) {
          return 0;
        }
        if (!isSearching && collapsedParentProjects[project?.parentProject]) {
          if (type !== 'favorites') {
            return 0;
          } else if (projectIds.find((id) => project?.parentProject === id)) {
            return 0;
          }
        }

        const accessRights = getProjectAccess(project, userMe, activeTab);

        const activeMenuItems = Object.keys(accessRights).reduce(
          (newObj, key) => {
            if (accessRights[key] === true) {
              newObj[key] = accessRights[key];
            }
            return newObj;
          },
          {}
        );

        if (!isSubMenu && project?.projectId === activeProjectId) {
          const menuItemsValues = Object.values(activeMenuItems);
          const menuItemsKeys = Object.keys(activeMenuItems);

          const financeCorrection: number = !isFinanceEnabled
            ? menuItemsKeys.filter(
                (item) =>
                  item === 'invoiceControlling' ||
                  item === 'timeRecordControlling' ||
                  item === 'timeRecord'
              ).length
            : 0;
          return 40 + (menuItemsValues.length - financeCorrection) * 40;
        }
        return 40;
      },
      [
        projectIds,
        getProjectById,
        activeProjectId,
        userMe,
        isSearching,
        isSubMenu,
        collapsedParentProjects,
        type,
        activeTab,
        isFinanceEnabled,
      ]
    );

    const rerenderProjectList = useCallback(() => {
      ref?.current?.recomputeRowHeights();
      ref?.current?.forceUpdate();
    }, [ref]);

    //#endregion

    //#region ------------------------------ Effects
    useImperativeHandle(componentRef, () => ({
      getCurrentProjects: () => {
        return projectIds.map((id) => getProjectById(id));
      },
    }));

    useEffect(() => {
      rerenderProjectList();
    }, [
      ref,
      activeProjectId,
      selectedSubModule,
      isSearching,
      type,
      projectIds,
      getProjectById,
      activeProject,
      calcRowHeight,
      rerenderProjectList,
    ]);

    if (projectIds?.length === 0) {
      if (isFetching) {
        return <ProjectsNavigationSkeleton />;
      }
      return (
        <div className={classes.noContent}>
          {t('projects:navigation.noContent')}
        </div>
      );
    }
    //endregion

    return (
      <div className={classes.root}>
        <AutoSizer>
          {({ height, width }) => (
            <List
              rowCount={projectIds.length}
              rowHeight={calcRowHeight}
              height={height}
              width={width}
              overscanRowCount={10}
              ref={ref}
              rowRenderer={({ index, key, style }) => {
                const project = getProjectById(projectIds[index]);
                const isParentProject =
                  !isSearching &&
                  collapsedParentProjects[project.projectId] !== undefined &&
                  parentProjectSubProjects[project.projectId]?.length > 0;
                if (
                  !project ||
                  (!isSearching && type === 'favorites' && !project.favorite)
                ) {
                  return null;
                }
                if (
                  !isSearching &&
                  collapsedParentProjects[project?.parentProject]
                ) {
                  if (type !== 'favorites') {
                    return null;
                  } else if (
                    projectIds.find((id) => project?.parentProject === id)
                  ) {
                    return null;
                  }
                }
                const isProjectMember = myProjectIdsSet.has(project.projectId);
                return (
                  <ProjectDropDownNavigationItem
                    project={project}
                    pathPrefix={pathPrefix}
                    isSubMenu={isSubMenu}
                    style={style}
                    activeProjectId={activeProjectId}
                    selectedSubModule={selectedSubModule}
                    key={key}
                    type={type}
                    isProjectMember={isProjectMember}
                    isParentProject={isParentProject}
                    isAnySubProjectFavorited={
                      isParentProject &&
                      parentProjectSubProjects[project.projectId]?.some(
                        (subProject) => getProjectById(subProject)?.favorite
                      )
                    }
                    activeTab={activeTab}
                    collapsed={collapsedParentProjects[project.projectId]}
                    subProjects={parentProjectSubProjects}
                    setCollapsed={setCollapseParentProject}
                    rerenderProjectList={rerenderProjectList}
                    getProjectNewsCountsByProjectId={
                      getProjectNewsCountsByProjectId
                    }
                    getProjectById={getProjectById}
                  />
                );
              }}
            />
          )}
        </AutoSizer>
      </div>
    );
  })
);

export default ProjectDropDownNavigation;
